class ProductMediaSlider extends HTMLElement {
  connectedCallback() {
    this._productId = this.dataset.id;
    this._viewport = this.querySelector(".product-media-slider__viewport");
    this._container = this.querySelector(".product-media--container");
    this._loader = this.querySelector(".product-media-slider__loader");
    this._prev = this.querySelector(".product-media-slider__arrow--prev");
    this._next = this.querySelector(".product-media-slider__arrow--next");
    this._carousel = this.dataset.carousel === "true";
    this._loadToken = 0;

    this._onVariant = () => this._queueSync();
    this._onMedia = () => this._queueSync();

    window.on(
      `theme:product:${this._productId}:variantChanged.productMediaSlider`,
      this._onVariant
    );
    window.on(
      `theme:product:${this._productId}:mediaChanged.productMediaSlider`,
      this._onMedia
    );

    this._onPrev = this._step.bind(this, -1);
    this._onNext = this._step.bind(this, 1);
    this._prev?.addEventListener("click", this._onPrev);
    this._next?.addEventListener("click", this._onNext);

    this._sync();
  }

  disconnectedCallback() {
    window.off(
      `theme:product:${this._productId}:variantChanged.productMediaSlider`
    );
    window.off(
      `theme:product:${this._productId}:mediaChanged.productMediaSlider`
    );
    this._prev?.removeEventListener("click", this._onPrev);
    this._next?.removeEventListener("click", this._onNext);
  }

  _queueSync() {
    requestAnimationFrame(() => this._sync());
  }

  _slides() {
    return this._container
      ? [...this._container.querySelectorAll(".product-media--root")]
      : [];
  }

  _step(delta) {
    const slides = this._slides();
    const idx = slides.findIndex((el) => el.getAttribute("data-active") === "true");
    if (idx < 0) return;
    const next = idx + delta;
    if (next < 0 || next >= slides.length) return;
    const id = parseInt(slides[next].dataset.id, 10);
    if (Number.isNaN(id)) return;
    window.trigger(`theme:product:${this._productId}:mediaChanged`, { id });
  }

  _sync() {
    const slides = this._slides();
    const idx = slides.findIndex((el) => el.getAttribute("data-active") === "true");
    const last = slides.length - 1;

    if (this._carousel && this._container && idx >= 0) {
      this._container.style.transform = `translate3d(-${idx * 100}%, 0, 0)`;
    }

    const hide = slides.length <= 1;
    [this._prev, this._next].forEach((btn) => {
      if (!btn) return;
      btn.hidden = hide;
      btn.setAttribute("aria-hidden", hide ? "true" : "false");
    });

    if (this._prev) this._prev.disabled = idx <= 0;
    if (this._next) this._next.disabled = idx < 0 || idx >= last;

    this._bindActiveSlideLoaders();
  }

  _bindActiveSlideLoaders() {
    if (!this._loader) return;

    this._loadToken += 1;
    const token = this._loadToken;

    const slides = this._slides();
    const idx = slides.findIndex((el) => el.getAttribute("data-active") === "true");
    if (idx < 0) {
      this._setLoadingUi(false);
      return;
    }

    const slide = slides[idx];
    const pending = this._pendingMedia(slide);

    if (pending.length === 0) {
      this._setLoadingUi(false);
      return;
    }

    this._setLoadingUi(true);

    const onResolved = () => {
      if (token !== this._loadToken) return;
      const stillPending = this._pendingMedia(slide);
      if (stillPending.length === 0) this._setLoadingUi(false);
    };

    pending.forEach((el) => {
      if (el.tagName === "IMG") {
        el.addEventListener("load", onResolved, { once: true });
        el.addEventListener("error", onResolved, { once: true });
      } else if (el.tagName === "VIDEO") {
        if (el.readyState >= 2) onResolved();
        else {
          el.addEventListener("loadeddata", onResolved, { once: true });
          el.addEventListener("error", onResolved, { once: true });
        }
      } else if (el.tagName === "MODEL-VIEWER") {
        if (el.loaded) onResolved();
        else el.addEventListener("load", onResolved, { once: true });
      }
    });

    requestAnimationFrame(() => {
      if (token !== this._loadToken) return;
      if (this._pendingMedia(slide).length === 0) this._setLoadingUi(false);
    });
  }

  _pendingMedia(slide) {
    const out = [];
    slide.querySelectorAll("img").forEach((img) => {
      if (!img.complete) out.push(img);
    });
    slide.querySelectorAll("video").forEach((v) => {
      if (v.readyState < 2) out.push(v);
    });
    slide.querySelectorAll("model-viewer").forEach((mv) => {
      if (!mv.loaded) out.push(mv);
    });
    return out;
  }

  _setLoadingUi(loading) {
    if (!this._loader) return;
    this._loader.hidden = !loading;
    this._loader.setAttribute("aria-hidden", loading ? "false" : "true");
    this.setAttribute("aria-busy", loading ? "true" : "false");
  }
}

customElements.define("product-media-slider", ProductMediaSlider);
